/**
 * ExportWorkspace — the customer **Export** screen (route `export`): the merged
 * Mockup Generator funnel + Batch + Queue + Export pipeline (formerly the "Studio"
 * workspace). The stages are one pipeline: you SELECT a product/brand/models in the
 * Mockup Generator (which auto-loads their saved templates), START a batch, watch it in
 * the QUEUE, then collect finished work under EXPORT. One screen, four sub-tabs. Uses the
 * shared `.workspace__*` tabbed-shell styling (also used by Editor + Dashboard).
 */
import { useEffect, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { Tabs } from "../ui/Tabs.js";
import { useSessionStore } from "../stores/session.js";
import { useSelectionStore } from "../stores/selection.js";
import { MockupCatalog } from "../mockups/BatchContents.js";
import { BatchPanel } from "../batch/BatchPanel.js";
import { QueuePanel } from "../queue/QueuePanel.js";
import { ExportPanel } from "../export/ExportPanel.js";
import "../export/export.css";

export type ExportTab = "select" | "batch" | "queue" | "export";

export interface ExportWorkspaceProps {
  onGoTemplates: () => void;
  /**
   * Which sub-tab to open on (WS-B B-9 / AUD-6 IMP-13). Defaults to Select.
   *
   * The Templates page's "Create Mock-up" stages a REAL selection and then routes here — and
   * landing on Select meant landing on `MockupCatalog`, which lists the templates synced to
   * *this device*. So the footer counted the selection the operator had just made while the
   * body said "Pick a model on the left" over an unpopulated rail. The selection was never
   * the problem; the tab was. Batch is the tab that can act on exactly that selection.
   */
  initialTab?: ExportTab;
}

const TABS: { key: ExportTab; label: string; hint: string }[] = [
  { key: "select", label: "Select", hint: "Product & models" },
  { key: "batch", label: "Batch", hint: "Start a batch" },
  { key: "queue", label: "Queue", hint: "In progress" },
  { key: "export", label: "Export", hint: "Finished" },
];

export function ExportWorkspace({ onGoTemplates, initialTab }: ExportWorkspaceProps) {
  const [tab, setTab] = useState<ExportTab>(initialTab ?? "select");
  // The workspace stays mounted across navigation, so a handoff that arrives later still
  // has to land on the tab it asked for.
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);
  const session = useSessionStore((s) => s.session);
  const selection = useSelectionStore((s) => s.selection);
  const batchSelections = useSelectionStore((s) => s.batchSelections);
  const primary = batchSelections[0] ?? selection;

  return (
    <section className="workspace" data-testid="export-workspace">
      <header className="workspace__head">
        {/* MI-10: this was "Mockups — select, batch & export." — the only heading in the app
            that ended in a full stop, sitting directly above the only Title-Case <h2>
            ("Mockup Generator"), which the four tab labels beneath already say. The h2 is
            gone and this is the one page title. The hyphenated spelling is settled in K2-18;
            it lands here so the two tasks do not fight over the same string. */}
        <h1 className="panel-title">Mock-ups</h1>
      </header>

      <Tabs
        tabs={TABS}
        active={tab}
        onChange={(k) => setTab(k as ExportTab)}
        label="Mock-ups"
        idPrefix="export"
      >
        {tab === "select" && <MockupCatalog onReady={() => setTab("batch")} />}
        {tab === "batch" &&
          (primary && session ? (
            <BatchPanel
              templateId={primary.templateId}
              versionId={primary.versionId}
              customerId={session.customerId}
              tenantId={session.tenantId}
              name={primary.name}
              templateVersion={primary.templateVersion}
              context={primary.context}
              // WS-P: the single-template path needs the catalog row's "published a bake" bit
              // too — it is the COMMON case, and without it the refusal would never fire here.
              bakeExpected={primary.bakeExpected}
              onViewQueue={() => setTab("queue")}
            />
          ) : (
            <ExportEmpty action="batch" onGo={onGoTemplates} onChoose={() => setTab("select")} />
          ))}
        {tab === "queue" && <QueuePanel />}
        {tab === "export" &&
          (selection ? (
            <ExportPanel context={selection.context} />
          ) : (
            <ExportEmpty action="export" onGo={onGoTemplates} />
          ))}
      </Tabs>
    </section>
  );
}

function ExportEmpty({
  action,
  onGo,
  onChoose,
}: {
  action: "batch" | "export";
  onGo: () => void;
  /** Batch tab only: jump to the in-workspace Select tab so the empty state isn't a
   *  dead end with no button (which read as "the Render button isn't functioning"). */
  onChoose?: () => void;
}) {
  const verb = action === "batch" ? "create mock-ups" : "export a sheet";
  return (
    <EmptyState
      icon="sparkle"
      title="No template selected"
      hint={`Pick a template (or generate one) to ${verb}.`}
      testId={`${action}-no-selection`}
      action={
        <div className="row empty-state__action">
          {onChoose && (
            <button type="button" className="btn btn--primary" onClick={onChoose} data-testid="goto-select">
              Choose a template
            </button>
          )}
          <button type="button" className={`btn${onChoose ? " btn--ghost" : " btn--primary"}`} onClick={onGo}>
            Go to Templates
          </button>
        </div>
      }
    />
  );
}
